import React, { useEffect, useState } from "react";
import Input from "../../../components/FormComponents/Input";
import Loader from "../../../Layouts/Loader/Loader";
import ErrorMessage from "../../../components/FormComponents/ErrorMessage";
import GenericActionBar from "../../../Layouts/GenericActionBar/GenericActionBar";
import Textarea from "../../../components/FormComponents/Textarea";
import SingleSelectBox from "../../../components/FormComponents/SingleSelectBox";
import useFetchData from "../../../hooks/useFetchData";
import useDirtyChecker from "../../../hooks/useDirtyChecker";
import axiosInstance, { handleApiError } from "../../../services/axiosService";
import { toastSuccess } from "../../../utils/toastUtils";
import MongoFieldsTable from "./MongoFieldsTable";

const MongoTemplateEditor = ({ file, templateData, setTemplateData }) => {
  const [isSaving, setIsSaving] = useState(false);
  const { isDirty, markClean } = useDirtyChecker(templateData);

  const { data: stateMachines, loading: stateMachineLoading } = useFetchData("/state-machines");

  const handleMongoTemplateChange = (key, value) => {
    setTemplateData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const payload = { ...templateData, name: file.name };
      const { message } = await axiosInstance.patch(`/mongo-template/${file.id}`, payload);
      toastSuccess(message || "Mongo template saved successfully.");
      markClean();
    } catch (err) {
      handleApiError(err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="d_flex flex_column">
      <GenericActionBar
        path={
          <div>
            <em>Mongo Templates</em> → <span>{file.name}</span>
          </div>
        }
        actions={[
          {
            key: "save",
            icon: "save",
            title: isSaving ? "Saving..." : "Save",
            onClick: isSaving ? undefined : handleSave
          }
        ]}
        options={{
          save: {
            className: `${isDirty ? "save_glowing" : ""} ${isSaving ? "disabled" : ""}`.trim()
          }
        }}
      />

      <div className="template_form_outer">
        <div className="d_flex gap_3">
          <Input
            label="Base Collections"
            value={templateData.base_collection}
            onChange={(e) => handleMongoTemplateChange("base_collection", e.target.value)}
          />

          <SingleSelectBox
            selectedData={templateData.source_state_machine}
            options={stateMachineLoading ? [] : stateMachines}
            onChange={(obj) => handleMongoTemplateChange("source_state_machine", obj.id)}
            placeholder="Select Workflow"
            label="Select Workflow"
          />
        </div>
        <div className="d_flex gap_3">
          <div className="w_100">
            <MongoFieldsTable
              fields={templateData.fields}
              onChange={(updatedFields) => handleMongoTemplateChange("fields", updatedFields)}
            />
          </div>
          <Textarea
            label="Pipeline Query"
            value={templateData.pipeline_query}
            resize="vertical"
            containerClassName={"w_100 mt_3"}
            onChange={(e) => handleMongoTemplateChange("pipeline_query", e.target.value)}
          />
        </div>
      </div>
    </div>
  );
};

const MongoTemplateViewer = ({ file }) => {
  const [templateData, setTemplateData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchTemplate = async () => {
    try {
      setIsLoading(true);
      const { data } = await axiosInstance.get(`/mongo-template/${file.id}`);
      setTemplateData(data);
    } catch (err) {
      setError("Failed to load Mongo template.");
      handleApiError(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file.id]);

  if (isLoading) return <Loader />;
  if (error) {
    return (
      <div className="d_flex justify_center align_center h_100">
        <ErrorMessage>{error}</ErrorMessage>
      </div>
    );
  }

  if (!templateData) return null;

  return <MongoTemplateEditor file={file} templateData={templateData} setTemplateData={setTemplateData} />;
};

export default MongoTemplateViewer;
